import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import useProjectStore from "../../store/projectStore";
import { projectsApi } from "../../api/projects";
import { useLookups } from "../../hooks/useLookups";
import { predesign, interpolateCurve, MACHINE_TYPES, NQ_TO_NS } from "../../utils/predesign";
import { statCurvesApi } from "../../api/statCurves";
import useSaveShortcut from "../../hooks/useSaveShortcut";

const INPUT_CLS = `w-36 text-sm text-right border border-gray-300 rounded-md px-3 py-1.5
  focus:outline-none focus:ring-1 focus:ring-bh-500 bg-white`;

const VARIANTS = [
  { value: "standard",     label: "Standard" },
  { value: "progressive",  label: "Progressive" },
  { value: "conservative", label: "Conservative" },
];

// ── sub-components ────────────────────────────────────────────────────────────

function Row({ label, symbol, unit, value, onChange, placeholder, note, readOnly }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <div>
        <span className="text-sm text-gray-700">{label}</span>
        {symbol && <span className="ml-1.5 text-xs text-gray-400 font-mono">{symbol}</span>}
        {note   && <span className="ml-2 text-[10px] text-gray-400 italic">{note}</span>}
      </div>
      <div className="flex items-center gap-2">
        {readOnly ? (
          <span className="text-sm text-gray-500 w-36 text-right">{value ?? "—"}</span>
        ) : (
          <input type="number" step="any"
            value={value ?? ""}
            onChange={e => onChange(e.target.value === "" ? null : Number(e.target.value))}
            placeholder={placeholder}
            className={INPUT_CLS}
          />
        )}
        {unit && <span className="text-xs text-gray-400 w-12">{unit}</span>}
      </div>
    </div>
  );
}

function ResultTile({ label, value, unit, highlight, sub }) {
  return (
    <div className={`rounded-xl px-4 py-3 text-center ${highlight ? "bg-bh-50 border border-bh-100" : "bg-gray-50"}`}>
      <p className="text-[10px] font-mono text-gray-400 mb-0.5">{label}</p>
      <p className={`text-base font-bold tabular-nums ${highlight ? "text-bh-800" : "text-gray-800"}`}>
        {value}
        {unit && <span className="text-xs font-normal text-gray-400 ml-0.5">{unit}</span>}
      </p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── page ──────────────────────────────────────────────────────────────────────

export default function LayoutToolPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { activeProject, setActiveProject } = useProjectStore();
  const { labelFor: labelForMachine } = useLookups("machine_type");

  const [cfg, setCfg]             = useState({ n: null, Q: null, H: null, n_stages: 1, variant: "standard" });
  const [statCurves, setStatCurves] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [saved,  setSaved]        = useState(false);
  const [error,  setError]        = useState(null);

  // Load project layout config
  useEffect(() => {
    const load = (lc = {}) => setCfg({
      n:        lc.n        ?? null,
      Q:        lc.Q        ?? null,
      H:        lc.H        ?? null,
      n_stages: lc.n_stages ?? 1,
      variant:  lc.variant  ?? "standard",
    });

    if (activeProject?.id === parseInt(projectId)) {
      load(activeProject.layout_config ?? {});
    } else {
      projectsApi.get(projectId).then(({ data }) => {
        setActiveProject(data);
        load(data.layout_config ?? {});
      });
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load stat curves once
  useEffect(() => {
    statCurvesApi.list().then(({ data }) => setStatCurves(data)).catch(() => {});
  }, []);

  const set = (k) => (v) => { setCfg(f => ({ ...f, [k]: v })); setSaved(false); };

  const machineType = useMemo(() => {
    const { n, Q, H, n_stages = 1 } = cfg;
    if (!n || !Q || !H) return "radial";
    const H_stage = H / Math.max(1, n_stages);
    const nq = n * Math.sqrt(Q) / Math.pow(H_stage, 0.75);
    return nq >= 70 ? "mixed_flow" : "radial";
  }, [cfg]);

  const results = useMemo(() => {
    const { n, Q, H, n_stages = 1, variant } = cfg;
    if (!n || !Q || !H || H <= 0 || Q <= 0 || n <= 0) return null;

    const H_stage = H / Math.max(1, n_stages);
    const nq = n * Math.sqrt(Q) / Math.pow(H_stage, 0.75);

    const interp = (name) => interpolateCurve(statCurves, name, variant, nq);

    return predesign({
      n, Q, H, n_stages, machineType,
      psi2_stat:  interp("psi2_vs_nq"),
      phi1_stat:  interp("phi1_vs_nq"),
      b2_d2_stat: interp("b2_d2_vs_nq"),
      beta2_stat: interp("beta2_vs_nq"),
      z_d2_stat:  interp("z_d2_vs_nq"),
    });
  }, [cfg, machineType, statCurves]);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const { data } = await projectsApi.update(projectId, { layout_config: cfg });
      setActiveProject({ ...activeProject, ...data });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.error || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  useSaveShortcut(handleSave, { disabled: saving });

  const project = activeProject?.id === parseInt(projectId) ? activeProject : null;
  const variantLabel = VARIANTS.find(v => v.value === cfg.variant)?.label ?? "Standard";
  const usingStatCurves = results?.source === "stat";

  return (
    <>
      <div className="bg-white border-b border-gray-200 px-6 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 py-3 transition-colors">
            <ChevronLeftIcon className="h-4 w-4" />
            Back
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-700 py-3 border-l-2 border-bh-500 pl-3">
            Layout Tool
          </span>
          {project?.machine_type_id && (
            <span className="text-xs font-medium text-bh-600 bg-bh-50 border border-bh-200 px-2 py-0.5 rounded-full">
              {labelForMachine(project.machine_type_id)}
            </span>
          )}
          {project?.name && (
            <span className="text-xs text-gray-400">{project.name}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {error && <p className="text-xs text-red-500">{error}</p>}
          <button onClick={handleSave} disabled={saving}
            className="text-sm bg-bh-600 text-white rounded-lg px-5 py-2 hover:bg-bh-700 disabled:opacity-50 transition-colors font-medium">
            {saving ? "Saving…" : saved ? "Saved ✓" : (
              <>{`Save `}<kbd className="text-[10px] font-sans font-normal opacity-55">Ctrl+S</kbd></>
            )}
          </button>
        </div>
      </div>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-xl space-y-5">

          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
              Design Point
            </p>
            <p className="text-xs text-gray-400 mb-4">
              Project-wide hydraulic target. Used as reference for all designs.
            </p>

            <Row label="Rotational speed" symbol="n"        unit="rpm"  value={cfg.n}        onChange={set("n")}        placeholder="e.g. 2950" />
            <Row label="Flow rate"        symbol="Q"        unit="m³/s" value={cfg.Q}        onChange={set("Q")}        placeholder="e.g. 0.028" />
            <Row label="Head"             symbol="H"        unit="m"    value={cfg.H}        onChange={set("H")}        placeholder="e.g. 40" />
            <Row label="Number of stages" symbol="n_stages" unit="—"    value={cfg.n_stages} onChange={set("n_stages")} placeholder="1" />

            {/* Variant selector — replaces the manual ψ input */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <span className="text-sm text-gray-700">Design variant</span>
                <span className="ml-2 text-[10px] text-gray-400 italic">from statistical curves</span>
              </div>
              <select
                value={cfg.variant}
                onChange={e => { setCfg(f => ({ ...f, variant: e.target.value })); setSaved(false); }}
                className={INPUT_CLS + " cursor-pointer"}
              >
                {VARIANTS.map(v => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            </div>

            {/* Correlation — auto-derived */}
            <div className="flex items-center justify-between py-3 border-t border-gray-100 mt-1">
              <div>
                <span className="text-sm text-gray-700">Correlation</span>
                <span className="ml-2 text-[10px] text-gray-400 italic">auto from nq</span>
              </div>
              <span className="text-sm text-gray-500">
                {MACHINE_TYPES.find(m => m.value === machineType)?.label ?? "—"}
              </span>
            </div>
          </div>

          {/* nq / ns */}
          {results && (
            <div className="bg-bh-50 border border-bh-100 rounded-xl px-5 py-1">
              <div className="flex items-center justify-between py-3 border-b border-bh-100">
                <div>
                  <span className="text-sm text-gray-700">Specific speed</span>
                  <span className="ml-1.5 text-xs text-gray-400 font-mono">n_q</span>
                  <span className="ml-2 text-[10px] text-gray-400 italic">
                    SI · per stage{results.n_stages > 1 ? ` (total ${results.nq_total.toFixed(1)})` : ""}
                  </span>
                </div>
                <span className="text-xl font-bold text-bh-800 tabular-nums font-mono">
                  {results.nq.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3">
                <div>
                  <span className="text-sm text-gray-700">Specific speed</span>
                  <span className="ml-1.5 text-xs text-gray-400 font-mono">n_s</span>
                  <span className="ml-2 text-[10px] text-gray-400 italic">US · rpm, GPM, ft · per stage</span>
                </div>
                <span className="text-xl font-bold text-bh-800 tabular-nums font-mono">
                  {(results.nq * NQ_TO_NS).toFixed(0)}
                </span>
              </div>
            </div>
          )}

          {/* Primary dimensionless coefficients from stat curves */}
          {results && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Dimensionless Coefficients
                </p>
                <span className={`text-[10px] rounded px-2 py-0.5 border ${
                  usingStatCurves
                    ? "text-bh-700 bg-bh-50 border-bh-200"
                    : "text-amber-600 bg-amber-50 border-amber-200"
                }`}>
                  {usingStatCurves ? `Stat curves · ${variantLabel}` : "Estimates · Gülich"}
                </span>
              </div>

              <div className="grid grid-cols-4 gap-2">
                <ResultTile label="ψ₂"     value={results.psi.toFixed(3)}   unit="—" highlight />
                <ResultTile label="φ₁"     value={results.phi.toFixed(3)}   unit="—" highlight />
                <ResultTile label="b₂/D₂"  value={results.b2_d2.toFixed(3)} unit="—" />
                <ResultTile label="β₂"     value={results.beta2.toFixed(1)} unit="°" />
              </div>
            </div>
          )}

          {/* Derived geometry */}
          {results ? (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Preliminary Sizing
              </p>

              {results.warn && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                  ⚠ {results.warn}
                </p>
              )}

              <div className={`grid gap-2 mb-2 ${results.z_mm != null ? "grid-cols-4" : "grid-cols-3"}`}>
                <ResultTile label="D₂" value={results.D2_mm.toFixed(0)} unit="mm" highlight />
                <ResultTile label="b₂" value={results.b2_mm.toFixed(1)} unit="mm" highlight />
                <ResultTile label="D₁" value={results.D1_mm.toFixed(0)} unit="mm" highlight />
                {results.z_mm != null && (
                  <ResultTile label="z" value={results.z_mm.toFixed(1)} unit="mm" />
                )}
              </div>
              <div className="grid grid-cols-1 gap-2">
                <ResultTile label="β₂" value={results.beta2.toFixed(1)} unit="°" />
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-6 text-center">
              <p className="text-sm text-gray-400">Enter n, Q and H to compute preliminary sizing.</p>
            </div>
          )}

        </div>
      </main>
    </>
  );
}
